<template>
	<div
		data-component="deal-pack-action-groups"
		v-if="getShowGroups"
	>
		<action-groups
			:groups="getGroups"
			@actionClick="onActionClick"
		/>
		<portal
			to="modal"
			v-if="editApprovalNoteIsVisible"
		>
			<modal-inner
				@close="toggleEditApprovalNote"
				heading="Admin approval note"
			>
				<validation-observer
					data-element="form"
					tag="form"
					ref="form"
				>
					<input-field
						name="Note"
						type="text"
						placeholder="Add a note for the teacher"
						v-model="dealPack.approvalNote"
					/>
					<actions
						:actions="getApprovalNoteActions"
						@actionClick="onActionClick"
					/>
				</validation-observer>
			</modal-inner>
		</portal>
		<portal
			to="modal"
			v-if="viewApprovalNoteIsVisible"
		>
			<modal-inner
				@close="toggleViewApprovalNote"
				heading="Admin approval note"
			>
				<div
					data-element="approval-note"
					v-html="dealPack.approvalNote"
				/>
			</modal-inner>
		</portal>
		<portal
			to="modal"
			v-if="unpublishDealPackConfirmationIsVisible"
		>
			<modal-inner
				@close="toggleUnpublishDealPackConfirmation"
				heading="Unpublish deal pack"
			>
				<confirmation-modal
					text="Unpublishing the deal pack will hide the deal pack from all students."
					confirmationText="Unpublish"
					confirmationActionId="unpublishDealPack"
					cancelActionId="toggleUnpublishDealPackConfirmation"
					@actionClick="onActionClick"
				/>
			</modal-inner>
		</portal>
		<portal
			to="modal"
			v-if="archiveDealPackConfirmationIsVisible"
		>
			<modal-inner
				@close="toggleArchiveDealPackConfirmation"
				heading="Archive deal pack"
			>
				<confirmation-modal
					text="Archiving the deal pack will hide the deal pack from new students."
					confirmationText="Archive"
					confirmationActionId="archiveDealPack"
					cancelActionId="toggleArchiveDealPackConfirmation"
					@actionClick="onActionClick"
				/>
			</modal-inner>
		</portal>
		<portal
			to="modal"
			v-if="deleteDealPackConfirmationIsVisible"
		>
			<modal-inner
				@close="toggleDeleteDealPackConfirmation"
				heading="Delete deal pack"
			>
				<confirmation-modal
					text="Deleting the deal pack will completely remove it from a teachers list of deal packs."
					confirmationText="Delete"
					confirmationActionId="deleteDealPack"
					cancelActionId="toggleDeleteDealPackConfirmation"
					@actionClick="onActionClick"
				/>
			</modal-inner>
		</portal>
	</div>
</template>

<script>

	import ActionGroups       from '@/components/ui/actionGroups/ActionGroups';
	import ModalInner         from '@/components/ui/modal/ModalInner';
	import InputField         from '@/components/forms/InputField';
	import Actions            from '@/components/ui/Actions';
	import ConfirmationModal  from '@/components/ui/modal/ConfirmationModal';
	import actionClick        from '@/mixins/actionClick';
	import routeParams        from '@/mixins/routeParams';
	import api                from '@/services/api';

	export default {
		components: {
			ActionGroups,
			ModalInner,
			InputField,
			Actions,
			ConfirmationModal
		},
		mixins: [actionClick, routeParams],
		props: {
			dealPack: {
				type: Object,
				default: undefined
			}
		},
		data: () => ({
			editApprovalNoteIsVisible: false,
			viewApprovalNoteIsVisible: false,
			unpublishDealPackConfirmationIsVisible: false,
			archiveDealPackConfirmationIsVisible: false,
			deleteDealPackConfirmationIsVisible: false
		}),
		computed: {
			getShowGroups () {
				return this.getGroups.filter((group) => {
					return group.rows.length > 0;
				}).length > 0;
			},
			getShowLinkStripe () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				return !this.$store.getters['auth/getCanPublishDealPacks'];
			},
			getShowRequestPublication () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				if (this.dealPack?.approval === 'pending') {
					return false;
				}
				if (this.dealPack?.approval === 'approved') {
					return false;
				}
				if (!this.$store.getters['auth/getCanPublishDealPacks']) {
					return false;
				}
				if (this.dealPack?.status !== 'draft') {
					return false;
				}
				return true;
			},
			getShowRequestedPublication () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				if (this.dealPack?.approval !== 'pending') {
					return false;
				}
				return true;
			},
			getShowApprovedForPublication () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				if (this.dealPack?.approval !== 'approved') {
					return false;
				}
				return true;
			},
			getShowRejectedForPublication () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				if (this.dealPack?.approval !== 'rejected') {
					return false;
				}
				return true;
			},
			getShowReview () {
				if (this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval !== 'pending') {
					return false;
				}
				return true;
			},
			getShowReviewedAndApproved () {
				if (this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval !== 'approved') {
					return false;
				}
				return true;
			},
			getShowReviewedAndRejected () {
				if (this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval !== 'rejected') {
					return false;
				}
				return true;
			},
			getApprovalNoteActions () {
				return {
					primary: [
						{
							text: 'Approve',
							actionId: 'approveDealPack'
						},
						{
							text: 'Reject',
							actionId: 'rejectDealPack'
						}
					]
				};
			},
			getShowPublish () {
				if (this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval === 'pending') {
					return false;
				}
				if (this.dealPack?.status !== 'draft') {
					return false;
				}
				return true;
			},
			getShowUnpublish () {
				if (this.getContext !== 'teaching' &&
						this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval === 'pending') {
					return false;
				}
				if (this.dealPack?.status !== 'published') {
					return false;
				}
				return true;
			},
			getShowRepublish () {
				if (this.getContext !== 'teaching') {
					return false;
				}
				if (this.dealPack?.approval !== 'approved') {
					return false;
				}
				if (this.dealPack?.status !== 'draft') {
					return false;
				}
				return true;
			},
			getShowArchive () {
				if (this.getContext !== 'teaching' &&
						this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval === 'pending') {
					return false;
				}
				if (this.dealPack?.status !== 'published') {
					return false;
				}
				return true;
			},
			getShowUnarchive () {
				if (this.getContext !== 'teaching' &&
						this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.status !== 'archived') {
					return false;
				}
				return true;
			},
			getShowDelete () {
				if (this.getContext !== 'teaching' &&
						this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.approval === 'pending') {
					return false;
				}
				if (this.dealPack?.status !== 'draft') {
					return false;
				}
				return true;
			},
			getShowRestore () {
				if (this.getContext !== 'admin') {
					return false;
				}
				if (this.dealPack?.status !== 'deleted') {
					return false;
				}
				return true;
			},
			getGroups () {
				return [
					{
						heading: 'Publishing',
						rows: [
							(this.getShowLinkStripe ?
								{
									infoText: 'Payment details not set up',
									isImportant: true,
									action: {
										text: 'Set up',
										route: '/teaching/payment'
									}
								} : false
							),
							(this.getShowRequestPublication ?
								{
									infoText: 'Not approved for publication by an admin',
									action: {
										text: 'Request',
										actionId: 'requestPublication'
									}
								} : false
							),
							(this.getShowRequestedPublication ?
								{
									infoText: 'Approval for publication by an admin requested'
								} : false
							),
							(this.getShowApprovedForPublication ?
								{
									infoText: 'Approval for publication by an admin granted',
									action: {
										text: 'View note',
										actionId: 'toggleViewApprovalNote'
									}
								} : false
							),
							(this.getShowRejectedForPublication ?
								{
									infoText: 'Approval for publication by an admin not granted',
									action: {
										text: 'View note',
										actionId: 'toggleViewApprovalNote'
									}
								} : false
							),
							(this.getShowReview ?
								{
									infoText: 'Admin approval requested',
									isImportant: true,
									action: {
										text: 'Review deal pack for publishing',
										actionId: 'toggleEditApprovalNote'
									}
								} : false
							),
							(this.getShowReviewedAndApproved ?
								{
									infoText: 'Admin approval granted',
									action: {
										text: 'View note',
										actionId: 'toggleViewApprovalNote'
									}
								} : false
							),
							(this.getShowReviewedAndRejected ?
								{
									infoText: 'Admin approval not granted',
									isImportant: true,
									action: {
										text: 'View note',
										actionId: 'toggleViewApprovalNote'
									}
								} : false
							),
							(this.getShowPublish ?
								{
									infoText: 'Not available to any students',
									action: {
										text: 'Publish',
										actionId: 'requestPublication'
									}
								} : false
							),
							(this.getShowUnpublish ?
								{
									infoText: 'Published and available to everyone',
									action: {
										text: 'Unpublish',
										actionId: 'toggleUnpublishDealPackConfirmation'
									}
								} : false
							),
							(this.getShowRepublish ?
								{
									infoText: 'Not available to any students',
									action: {
										text: 'Republish',
										actionId: 'requestPublication'
									}
								} : false
							)
						].filter(Boolean)
					},
					{
						heading: 'Archiving',
						rows: [
							(this.getShowArchive ?
								{
									infoText: 'Available to new students',
									action: {
										text: 'Archive',
										actionId: 'toggleArchiveDealPackConfirmation'
									}
								} : false
							),
							(this.getShowUnarchive ?
								{
									infoText: 'Only available to current students',
									action: {
										text: 'Unarchive',
										actionId: 'unarchiveDealPack'
									}
								} : false
							)
						].filter(Boolean)
					},
					{
						heading: 'Deleting',
						rows: [
							(this.getShowDelete ?
								{
									infoText: 'Can only be undone by an admin',
									action: {
										text: 'Delete',
										actionId: 'toggleDeleteDealPackConfirmation'
									}
								} : false
							),
							(this.getShowRestore ?
								{
									infoText: 'Has been deleted',
									action: {
										text: 'Restore',
										actionId: 'restoreDealPack'
									}
								} : false
							)
						].filter(Boolean)
					}
				];
			}
		},
		methods: {
			async requestPublication () {
				const dealPack = await api[this.getApiRoot].requestPublicationDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.dealPack.approval = dealPack.approval;
				if (dealPack.status !== 'published') {
					return this.$store.commit('ui/showNotification', {
						notification: 'Deal pack has been sent for approval.'
					});
				}
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been published without needing approval.'
				});
			},
			toggleEditApprovalNote () {
				this.editApprovalNoteIsVisible = !this.editApprovalNoteIsVisible;
			},
			toggleViewApprovalNote () {
				this.viewApprovalNoteIsVisible = !this.viewApprovalNoteIsVisible;
			},
			toggleUnpublishDealPackConfirmation () {
				this.unpublishDealPackConfirmationIsVisible = !this.unpublishDealPackConfirmationIsVisible;
			},
			toggleArchiveDealPackConfirmation () {
				this.archiveDealPackConfirmationIsVisible = !this.archiveDealPackConfirmationIsVisible;
			},
			toggleDeleteDealPackConfirmation () {
				this.deleteDealPackConfirmationIsVisible = !this.deleteDealPackConfirmationIsVisible;
			},
			async approveDealPack () {
				const dealPack = await api.admin.approvePublicationDealPackById({
					dealPackId: this.getDealPackId,
					dealPack: {
						approvalNote: this.dealPack.approvalNote
					}
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.dealPack.approval = dealPack.approval;
				this.dealPack.approvalNote = dealPack.approvalNote;
				this.editApprovalNoteIsVisible = false;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been approved.'
				});
			},
			async rejectDealPack () {
				const dealPack = await api.admin.rejectPublicationDealPackById({
					dealPackId: this.getDealPackId,
					dealPack: {
						approvalNote: this.dealPack.approvalNote
					}
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.dealPack.approval = dealPack.approval;
				this.dealPack.approvalNote = dealPack.approvalNote;
				this.editApprovalNoteIsVisible = false;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been rejected.'
				});
			},
			async unpublishDealPack () {
				this.toggleUnpublishDealPackConfirmation();
				const dealPack = await api[this.getApiRoot].unpublishDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been unpublished.'
				});
			},
			async archiveDealPack () {
				this.toggleArchiveDealPackConfirmation();
				const dealPack = await api[this.getApiRoot].archiveDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been archived.'
				});
			},
			async unarchiveDealPack () {
				const dealPack = await api[this.getApiRoot].unarchiveDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been unarchived.'
				});
			},
			async deleteDealPack () {
				this.toggleDeleteDealPackConfirmation();
				const dealPack = await api[this.getApiRoot].deleteDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				if (this.entryRoute === 'teaching') {
					await this.$router.push(this.getBreadcrumbRoute);
				}
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been deleted.'
				});
			},
			async restoreDealPack () {
				const dealPack = await api.admin.unDeleteDealPackById({
					dealPackId: this.getDealPackId
				});
				if (!dealPack) {
					return false;
				}
				this.dealPack.status = dealPack.status;
				this.$store.commit('ui/showNotification', {
					notification: 'Deal pack has been restored.'
				});
			}
		}
	};

</script>

<style lang="scss" scoped>
	::v-deep [data-element='approval-note'] {
		font-size:rem(18);
		@include rich-text-content;
		@include rich-text-suits;
	}
	[data-component='deal-pack-action-groups'] {
	}
</style>
